// Products — which platform product a rule / movement / event belongs to
// (MMS_POINTS_HUB_PLAN F1). The backend resolves `product` (products.py); this
// file only names and colours them, and draws the filter + the bar chart.
import { useEffect, useRef, useState } from 'react';
import { t } from '../lib/i18n';

export const PRODUCTS = ['kms', 'lms', 'pms', 'platform', 'referral', 'other'];

export const PRODUCT_LABEL = {
  kms: 'KMS', lms: 'LMS', pms: 'PMS', platform: 'Platform', referral: 'Referrals', other: 'Other',
};

// One colour per product, from the theme tokens so it reads in light and dark.
export const PRODUCT_COLOR = {
  kms: 'var(--mms-info)',
  lms: 'var(--mms-success)',
  pms: 'var(--mms-warning)',
  platform: 'var(--mms-sidebar)',
  referral: '#8b5cf6', // violet: distinct from LMS green in both themes
  other: 'var(--mms-faint)',
};

export function ProductBadge({ product }) {
  const p = product || 'other';
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-mms-muted">
      <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full" style={{ background: PRODUCT_COLOR[p] }} />
      {t(PRODUCT_LABEL[p] || p)}
    </span>
  );
}

// Chip row: "All" + one chip per product. value '' = all.
export function ProductFilter({ value, onChange, counts }) {
  const chip = (key, label, color) => {
    const on = value === key;
    return (
      <button
        key={key || 'all'}
        type="button"
        onClick={() => onChange(key)}
        aria-pressed={on}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
          on ? 'border-[var(--mms-accent)] text-mms-text bg-mms-surface' : 'border-mms-border text-mms-muted hover:text-mms-text'
        }`}
      >
        {color ? <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full" style={{ background: color }} /> : null}
        {label}
        {counts && counts[key || 'all'] != null ? <span className="mms-num text-mms-faint">{counts[key || 'all']}</span> : null}
      </button>
    );
  };
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={t('Filter by product')}>
      {chip('', t('All products'))}
      {PRODUCTS.map((p) => chip(p, t(PRODUCT_LABEL[p]), PRODUCT_COLOR[p]))}
    </div>
  );
}

// Stacked monthly bars. rows = [{label, <key>: n, …}], keys = [{key, label, color}].
export function StackedBars({ rows, keys, height = 220, ariaLabel }) {
  // Draw at the container's real width so 11px labels stay 11px on any screen.
  const boxRef = useRef(null);
  const [W, setW] = useState(640);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = height;
  const padL = 44;
  const padB = 28;
  const padT = 10;
  const totals = rows.map((r) => keys.reduce((s, k) => s + (Number(r[k.key]) || 0), 0));
  const rawMax = Math.max(1, ...totals);
  const step = niceStep(rawMax / 4);
  const max = Math.ceil(rawMax / step) * step;
  const innerW = W - padL - 8;
  const innerH = H - padB - padT;
  const slot = innerW / Math.max(1, rows.length);
  const barW = Math.min(46, slot * 0.6);
  const y = (v) => padT + innerH - (v / max) * innerH;
  const ticks = [];
  for (let v = 0; v <= max; v += step) ticks.push(v);
  return (
    <div ref={boxRef}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel} className="block max-w-full">
        {ticks.map((v) => (
          <g key={v}>
            <line x1={padL} x2={W - 8} y1={y(v)} y2={y(v)} stroke="var(--mms-border)" strokeWidth="1" />
            <text x={padL - 6} y={y(v) + 4} textAnchor="end" fontSize="11" fill="var(--mms-faint)">{v.toLocaleString()}</text>
          </g>
        ))}
        {rows.map((r, i) => {
          let acc = 0;
          const x = padL + slot * i + (slot - barW) / 2;
          return (
            <g key={r.label || i}>
              {keys.map((k) => {
                const v = Number(r[k.key]) || 0;
                if (!v) return null;
                const top = y(acc + v);
                const h = y(acc) - top;
                acc += v;
                return <rect key={k.key} x={x} y={top} width={barW} height={Math.max(0, h)} fill={k.color}><title>{`${r.label} · ${k.label}: ${v.toLocaleString()}`}</title></rect>;
              })}
              <text x={x + barW / 2} y={H - 9} textAnchor="middle" fontSize="11" fill="var(--mms-muted)">{shortMonth(r.label)}</text>
            </g>
          );
        })}
      </svg>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {keys.map((k) => (
          <span key={k.key} className="inline-flex items-center gap-1.5 text-xs text-mms-muted">
            <span aria-hidden="true" className="inline-block h-2 w-2 rounded-sm" style={{ background: k.color }} />{k.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function niceStep(x) {
  if (x <= 1) return 1;
  const p = 10 ** Math.floor(Math.log10(x));
  const m = x / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
}

// "Oct 2026" → "Oct 26" so a year of bars fits on a narrow screen.
function shortMonth(label) {
  const m = /^(\w{3}) (\d{4})$/.exec(label || '');
  return m ? `${m[1]} ${m[2].slice(2)}` : label;
}
